import React, { useEffect, useState } from 'react';
import { getUserById } from '../../service/user';
import { updateUser } from '../../service/user';

// Material-UI Components
import {
    Box, Button, Card, CardContent, Container, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, Grid, IconButton, Paper, Snackbar, TextField, Typography,
    useTheme, Avatar, CircularProgress, FormControl, InputLabel,
    Select, MenuItem,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Person as PersonIcon,
    Close as CloseIcon,
    Check as CheckIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate } from "react-router-dom";

const Profile = () => {
    const theme = useTheme();
    const userId = localStorage.getItem("user_id");
    const navigate = useNavigate();

    const [openProfileDialog, setOpenProfileDialog] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [loading, setLoading] = useState(true);

    const [profileFormData, setProfileFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        age: '',
        yearOfBirth: '',
        gender: '',
        marital_status: '',
        userData: '',
        created_at: ''
    });

    // Fetch user data
    const fetchUser = async () => {
        try {
            if (!userId) {
                console.error("No user_id found in localStorage");
                return;
            }
            const userData = await getUserById(userId);
            setProfileFormData({
                name: userData.name || '',
                email: userData.email || '',
                phone: userData.phone || '',
                address: userData.address || '',
                age: userData.age ? userData.age.toString() : '',
                yearOfBirth: userData?.year_of_birth || '',
                gender: userData.gender || '',
                marital_status: userData.marital_status || '',
                account_id: userData.account_id || '',
                created_at: userData.created_at || ''
            });
        } catch (error) {
            console.error("Failed to fetch user:", error);
            showSnackbar('Failed to load user data', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUser();
    }, [userId]);

    const handleEditProfile = () => {
        setOpenProfileDialog(true);
    };

    const handleSaveProfile = async () => {
        try {
            if (!userId) {
                showSnackbar('User ID not found. Please login again.', 'error');
                return;
            }

            if (
                profileFormData.phone &&
                profileFormData.phone.length !== 10
            ) {
                showSnackbar("Phone number must be exactly 10 digits", "error");
                return;
            }

            // 🛠️ Call API
            const updatedUser = await updateUser(userId, profileFormData);

            showSnackbar('Profile updated successfully', 'success');
            setOpenProfileDialog(false);

            fetchUser();

        } catch (error) {
            console.error("Profile update failed:", error);
            const errorMsg = error?.message || error?.error || "Failed to update profile";
            showSnackbar(errorMsg, 'error');
        }
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Avatar with initials
    const getInitials = (name) => {
        return name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : '';
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)', }}>
            <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* Header */}
                <Box sx={{ mb: 4 }}>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, justifyContent: "space-between" }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                            My Profile
                        </Typography>
                        <IconButton
                            onClick={() => navigate(-1)}
                            sx={{
                                backgroundColor: '#F0F5FF',
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                color: '#0B2D6B',
                                '&:hover': {
                                    backgroundColor: '#E0EBFF'
                                }
                            }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    </Box>
                </Box>

                <Card
                    sx={{
                        mb: 4,
                        borderRadius: 4,
                        boxShadow: 4,
                        overflow: 'hidden',
                        background: 'linear-gradient(to bottom, #f9f9f9, #ffffff)',
                    }}
                >
                    <Box
                        sx={{
                            p: 4,
                            py: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 3,
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 70,
                                height: 70,
                                fontSize: '1.5rem',
                                border: '4px solid rgba(255,255,255,0.7)',
                                background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)'
                            }}
                        >
                            {getInitials(profileFormData.name)}
                        </Avatar>

                        <Box sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            width: "100%",
                            gap: 3,
                            position: "relative",
                            pt: 2
                        }}>

                            <Box sx={{
                                display: "flex",
                                flexDirection: "column",
                                flexGrow: 1,
                                mt: 1
                            }}>
                                <Typography variant="h5">
                                    {profileFormData.name}
                                </Typography>
                                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                                    Member since{" "}
                                    {new Date(profileFormData.created_at).toLocaleDateString("en-GB", {
                                        day: "numeric",
                                        month: "short",
                                        year: "numeric",
                                    })}
                                </Typography>
                            </Box>

                            <Button
                                variant="contained"
                                onClick={handleEditProfile}
                                sx={{
                                    textTransform: "none",
                                    borderRadius: 3,
                                    px: 3,
                                    position: "absolute",
                                    top: 0,
                                    right: 0,
                                    background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)'
                                }}
                            >
                                Edit
                            </Button>
                        </Box>
                    </Box>

                    <Divider sx={{ mb: 4 }} />

                    <CardContent sx={{ p: 4, pt: 0 }}>
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={6}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        bgcolor: 'white',
                                        border: '1px solid #e0e0e0',
                                    }}
                                >
                                    <Typography variant="h6" color="primary" gutterBottom>
                                        <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        Contact Information
                                    </Typography>

                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Email Address
                                        </Typography>
                                        <Typography variant="body1" fontWeight="500">
                                            {profileFormData.email || 'Not provided'}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Phone Number
                                        </Typography>
                                        <Typography variant="body1" fontWeight="500">
                                            {profileFormData.phone || 'Not provided'}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        {/* <Typography variant="caption" color="text.secondary">
                                            Address
                                        </Typography>
                                        <Typography variant="body1" fontWeight="500">
                                            {profileFormData.address || 'Not provided'}
                                        </Typography> */}
                                    </Box>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        bgcolor: 'white',
                                        border: '1px solid #e0e0e0',
                                    }}
                                >

                                    <Typography variant="h6" color="primary" gutterBottom>
                                        <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        Personal Details
                                    </Typography>

                                    <Grid container spacing={2} mb={3}>
                                        <Grid item xs={6}>
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 3,
                                                    textAlign: 'center',
                                                    borderRadius: 3,
                                                    bgcolor: 'grey.50',
                                                    border: '1px solid #e0e0e0',
                                                }}
                                            >
                                                <Typography variant="h5" color="primary" fontWeight="bold">
                                                    {profileFormData.age || '--'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Years Old
                                                </Typography>
                                            </Paper>
                                        </Grid>

                                        <Grid item xs={6}>
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 3,
                                                    textAlign: 'center',
                                                    borderRadius: 3,
                                                    bgcolor: 'grey.50',
                                                    border: '1px solid #e0e0e0',
                                                }}
                                            >
                                                <Typography variant="h5" color="primary" fontWeight="bold">
                                                    {profileFormData.gender
                                                        ? profileFormData.gender.charAt(0).toUpperCase() +
                                                        profileFormData.gender.slice(1)
                                                        : '--'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Gender
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>

                                    <Box>
                                        {/* <Typography variant="caption" color="text.secondary">
                                            Marital Status
                                        </Typography>
                                        <Typography variant="body1" fontWeight="500">
                                            {profileFormData.marital_status
                                                ? profileFormData.marital_status.charAt(0).toUpperCase() +
                                                profileFormData.marital_status.slice(1)
                                                : 'Not specified'}
                                        </Typography> */}
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Edit Profile Dialog */}
                <Dialog
                    open={openProfileDialog}
                    onClose={() => setOpenProfileDialog(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Edit Profile</DialogTitle>
                    <IconButton
                        aria-label="close"
                        onClick={() => setOpenProfileDialog(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <DialogContent dividers>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Full Name"
                                    fullWidth
                                    margin="normal"
                                    required
                                    value={profileFormData.name}
                                    onChange={(e) => setProfileFormData({ ...profileFormData, name: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Email Address"
                                    type="email"
                                    fullWidth
                                    margin="normal"
                                    required
                                    disabled
                                    value={profileFormData.email}
                                    sx={{ bgcolor: 'grey.100' }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Phone Number"
                                    type="tel"
                                    fullWidth
                                    margin="normal"
                                    required
                                    value={profileFormData.phone}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, ""); // allow only digits

                                        if (value.length <= 10) {
                                            setProfileFormData({
                                                ...profileFormData,
                                                phone: value,
                                            });
                                        }
                                    }}
                                    error={
                                        profileFormData.phone !== "" &&
                                        profileFormData.phone.length !== 10
                                    }
                                    helperText={
                                        profileFormData.phone !== "" &&
                                            profileFormData.phone.length !== 10
                                            ? "Phone number must be exactly 10 digits"
                                            : ""
                                    }
                                    inputProps={{
                                        maxLength: 10,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Age"
                                    type="number"
                                    fullWidth
                                    margin="normal"
                                    value={profileFormData.age}
                                    onChange={(e) => setProfileFormData({ ...profileFormData, age: e.target.value })}
                                    inputProps={{ min: 1, max: 120 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Gender</InputLabel>
                                    <Select
                                        value={profileFormData.gender}
                                        label="Gender"
                                        onChange={(e) => setProfileFormData({ ...profileFormData, gender: e.target.value })}
                                    >
                                        <MenuItem value="">Select gender</MenuItem>
                                        <MenuItem value="male">Male</MenuItem>
                                        <MenuItem value="female">Female</MenuItem>
                                        <MenuItem value="other">Other</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Marital Status</InputLabel>
                                    <Select
                                        value={profileFormData.marital_status}
                                        label="Marital Status"
                                        onChange={(e) => setProfileFormData({ ...profileFormData, marital_status: e.target.value })}
                                    >
                                        <MenuItem value="">Select status</MenuItem>
                                        <MenuItem value="single">Single</MenuItem>
                                        <MenuItem value="married">Married</MenuItem>
                                        <MenuItem value="divorced">Divorced</MenuItem>
                                        <MenuItem value="widowed">Widowed</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Address"
                                    multiline
                                    rows={2}
                                    fullWidth
                                    margin="normal"
                                    value={profileFormData.address}
                                    onChange={(e) => setProfileFormData({ ...profileFormData, address: e.target.value })}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setOpenProfileDialog(false)}>Cancel</Button>
                        <Button
                            onClick={handleSaveProfile}
                            variant="contained"
                            disableElevation
                            sx={{ background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)', color: 'white' }}
                        >
                            Save Changes
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: 'background.paper',
                            color: snackbar.severity === 'error' ? 'error.main' : 'success.main',
                            p: 2,
                            borderRadius: 1,
                            boxShadow: 3,
                            minWidth: 300,
                            borderLeft: `4px solid ${snackbar.severity === 'error' ? theme.palette.error.main : theme.palette.success.main}`,
                        }}
                    >
                        {snackbar.severity === 'error' ? (
                            <WarningIcon sx={{ mr: 1 }} />
                        ) : (
                            <CheckIcon sx={{ mr: 1 }} />
                        )}
                        <Typography variant="body2">{snackbar.message}</Typography>
                        <IconButton
                            size="small"
                            aria-label="close"
                            color="inherit"
                            onClick={handleCloseSnackbar}
                            sx={{ ml: 'auto' }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Snackbar>
            </Container>
        </Box>
    );
};

export default Profile;
